import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { keyframes } from "@mui/system";

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-8px); }
`;

const floatCentered = keyframes`
  0%, 100% { transform: translateX(-50%) translateY(0px); }
  50%       { transform: translateX(-50%) translateY(-8px); }
`;

const fmt = (v) =>
  "₹" + new Intl.NumberFormat("en-IN").format(v || 0);

const fmtShort = (v) => {
  if (v >= 1000) return "₹" + v / 1000 + "k";
  return "₹" + v;
};

const BookingsLadderCard = ({ data, currentQuarter, dashboardLoading }) => {
  // booking_count from the dashboard-summary API is authoritative —
  // it counts all qualifying bookings regardless of reward type (basic/incentive only/mixed)
  const currentBookings = currentQuarter?.booking_count ?? 0;
  const earnedThisQuarter =
    (currentQuarter?.basic_redeemable || 0) +
    (currentQuarter?.incentive_redeemable || 0) +
    (currentQuarter?.incentive_locked || 0);
  const loading = dashboardLoading;

  // Derive slot state purely from currentBookings
  const totalSlots = data.slots.length;
  const bossReached = currentBookings >= totalSlots;
  const slots = data.slots.map((slot, i) => ({
    ...slot,
    reached: i < currentBookings,
    isNext: i === currentBookings && !bossReached,
    isBoss: i === totalSlots - 1,
  }));

  const nextSlot = slots.find((s) => s.isNext);
  const bossSlot = slots.find((s) => s.isBoss);
  const nextAway = nextSlot ? slots.indexOf(nextSlot) + 1 - currentBookings : 0;
  const bossAway = bossSlot && !bossReached ? Math.max(0, slots.indexOf(bossSlot) + 1 - currentBookings) : 0;
  const beyondTarget = bossReached ? currentBookings - totalSlots : 0;

  return (
    <Box
      sx={{
        background:
          "radial-gradient(700px 320px at 100% 0%, rgba(255,255,255,.18), transparent 60%), linear-gradient(135deg, #B45309 0%, #F59E0B 55%, #FCD34D 100%)",
        borderRadius: 3,
        p: 2.5,
        color: "#fff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box mb={2}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography sx={{ fontSize: "11px", fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>
            🏆 Booking incentive
          </Typography>
          <Box sx={{ bgcolor: "rgba(255,255,255,.15)", px: 1.5, py: 0.25, borderRadius: "999px", fontSize: "11px", fontWeight: 600 }}>
            Primary earnings driver
          </Box>
        </Box>
        <Box display="flex" alignItems="flex-end" gap={1.5} flexWrap="wrap">
          <Box display="flex" alignItems="baseline" gap={0.5}>
            {loading ? (
              <CircularProgress size={28} sx={{ color: "rgba(255,255,255,.70)" }} />
            ) : (
              <Typography sx={{ fontSize: "44px", fontWeight: 800, lineHeight: 1 }}>
                {currentBookings}
              </Typography>
            )}
            <Typography sx={{ fontSize: "28px", fontWeight: 600, opacity: 0.6 }}>
              / {data.quarterTarget}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,.85)", mb: 0.5 }}>
            bookings this quarter · <strong>{loading ? "..." : fmt(earnedThisQuarter)}</strong> earned
          </Typography>
        </Box>
      </Box>

      {/* Ladder block */}
      <Box sx={{ bgcolor: "rgba(255,255,255,.10)", borderRadius: 2, p: 2, border: "1px solid rgba(255,255,255,.20)", flex: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography sx={{ fontSize: "12px", fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>
            Booking ladder · earn more per booking
          </Typography>
          {nextSlot && !loading && (
            <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,.85)" }}>
              Next tier <strong>{fmt(nextSlot.amount)}</strong>
            </Typography>
          )}
        </Box>

        {/* Bar grid */}
        <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${slots.length}, 1fr)`, gap: 1, alignItems: "flex-end" }}>
          {slots.map((slot) => (
            <Box key={slot.label} textAlign="center" sx={{ position: "relative" }}>
              {slot.isNext && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -12,
                    left: "50%",
                    bgcolor: "#fff",
                    color: "#B45309",
                    fontSize: "9px",
                    fontWeight: 800,
                    px: 1,
                    py: 0.25,
                    borderRadius: "999px",
                    whiteSpace: "nowrap",
                    boxShadow: "0 2px 8px rgba(0,0,0,.15)",
                    zIndex: 2,
                    transform: "translateX(-50%)",
                  }}
                >
                  NEXT STEP
                </Box>
              )}

              {slot.reached && slot.isBoss ? (
                <Box
                  sx={{
                    height: 56,
                    borderRadius: "8px",
                    background: "linear-gradient(135deg, #92400e, #d97706, #fde68a)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "22px",
                    boxShadow: "0 4px 16px rgba(0,0,0,.30)",
                  }}
                >
                  👑
                </Box>
              ) : slot.reached ? (
                <Box
                  sx={{
                    height: 56,
                    borderRadius: "8px",
                    bgcolor: "#10B981",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#fff",
                  }}
                >
                  ✓
                </Box>
              ) : slot.isNext ? (
                
                <Box
                  sx={{
                    height: 68,
                    borderRadius: "8px",
                    bgcolor: "#fff",
                    color: "#B45309",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "28px",
                    fontWeight: 800,
                    border: "4px solid rgba(180,83,9,.35)",
                    boxShadow: "0 8px 24px rgba(0,0,0,.20)",
                    animation: `${float} 6.5s ease-in-out infinite`,
                  }}
                >
                  {slots.indexOf(slot) + 1}
                </Box>
              ) : (
                <Box
                  sx={{
                    height: 56,
                    borderRadius: "8px",
                    bgcolor: "rgba(255,255,255,.10)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "16px",
                    color: "rgba(255,255,255,.60)",
                  }}
                >
                  🔒
                </Box>
              )}

              <Typography sx={{ fontSize: "11px", mt: 0.75, fontWeight: 700, color: slot.reached ? "#6ee7b7" : "#fff" }}>
                {fmtShort(slot.amount)}
              </Typography>
              <Typography sx={{ fontSize: "9px", color: "rgba(255,255,255,.70)", fontWeight: slot.isBoss ? 800 : 400 }}>
                {slot.isBoss ? "BOSS" : slot.isNext ? `${slot.label} · ${nextAway} to go` : slot.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Info cards */}
        {bossReached ? (
          /* ── Boss Tier Achieved banner ── */
          <Box
            sx={{
              mt: 2,
              background: "linear-gradient(135deg, rgba(255,255,255,.22), rgba(255,255,255,.08))",
              border: "2px solid rgba(255,255,255,.40)",
              borderRadius: 2,
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* faint watermark crown */}
            <Typography sx={{ position: "absolute", right: -10, top: -10, fontSize: "72px", opacity: 0.12, lineHeight: 1 }}>👑</Typography>

            {/* crown icon box */}
            <Box
              sx={{
                width: 56, height: 56, flexShrink: 0,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #92400e, #d97706, #fde68a)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "28px",
                boxShadow: "0 6px 20px rgba(0,0,0,.25)",
                animation: `${float} 6.5s ease-in-out infinite`,
              }}
            >
              👑
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 800, color: "#fde68a" }}>
                Boss Tier Achieved 🎉
              </Box>
              <Typography sx={{ fontSize: "17px", fontWeight: 800, mt: 0.25 }}>
                Earning {fmt(bossSlot?.amount)} per booking!
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,.80)", mt: 0.25 }}>
                {currentBookings} bookings done
                {beyondTarget > 0 && (
                  <Box component="span" sx={{ ml: 1, bgcolor: "rgba(52,211,153,.20)", color: "#6ee7b7", px: 1, py: 0.15, borderRadius: "999px", fontWeight: 700 }}>
                    +{beyondTarget} beyond target
                  </Box>
                )}
              </Typography>
            </Box>
          </Box>
        ) : (
          /* ── Normal: next milestone + boss tier ── */
          <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" }, gap: 1.5, alignItems: "stretch" }}>
            {nextSlot && (
              <Box sx={{ bgcolor: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.30)", borderRadius: 2, p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: "8px", bgcolor: "#fff", color: "#B45309", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", fontWeight: 800, flexShrink: 0, boxShadow: "0 4px 12px rgba(0,0,0,.15)", animation: `${float} 6.5s ease-in-out infinite` }}>
                  {slots.indexOf(nextSlot) + 1}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, color: "#fde68a" }}>Next milestone</Typography>
                  <Typography sx={{ fontSize: "15px", fontWeight: 800 }}>{nextAway} booking{nextAway !== 1 ? "s" : ""} away</Typography>
                  <Typography sx={{ fontSize: "11px", color: "#fde68a" }}>unlocks <strong style={{ color: "#fff" }}>{fmt(nextSlot.amount)}</strong> per booking</Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", color: "rgba(255,255,255,.50)", fontSize: "20px" }}>→</Box>

            {bossSlot && (
              <Box sx={{ background: "linear-gradient(135deg, rgba(255,255,255,.25), rgba(255,255,255,.10))", border: "1px solid rgba(255,255,255,.30)", borderRadius: 2, p: 1.5, display: "flex", alignItems: "center", gap: 1.5, position: "relative", overflow: "hidden" }}>
                <Typography sx={{ position: "absolute", right: -8, top: -8, fontSize: "36px", opacity: 0.20 }}>👑</Typography>
                <Box sx={{ width: 40, height: 40, borderRadius: "8px", bgcolor: "#fde68a", color: "#78350f", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: 800, flexShrink: 0, boxShadow: "0 4px 12px rgba(0,0,0,.15)" }}>
                  {bossSlot.label}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, color: "#fde68a" }}>Boss tier</Typography>
                  <Typography sx={{ fontSize: "15px", fontWeight: 800 }}>{bossAway} booking{bossAway !== 1 ? "s" : ""} away</Typography>
                  <Typography sx={{ fontSize: "11px", color: "#fde68a" }}>jumps to <strong style={{ color: "#fff" }}>{fmt(bossSlot.amount)}</strong> per booking</Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BookingsLadderCard;
